import { ListingController } from 'api';
import { AuthContext } from 'contexts';
import React from 'react';
import { buildPusherChannel } from 'utils';
import { Channel, LISTING_COUNTS_PREFIX } from 'utils/buildPusherChannel';
import { getDisplayedPreLaunchBanner } from '../util';

const useConfiguredCounts = () => {
  const {
    pusher: pusherInstance,
    auth,
    onboarding,
    orgStatus,
  } = React.useContext(AuthContext);
  const [counts, setCounts] = React.useState<{
    configuredCount: number;
    totalCount: number;
  }>();

  const [listingCountChannel, setListingCountChannel] =
    React.useState<Channel>();

  React.useEffect(() => {
    if (getDisplayedPreLaunchBanner(auth, orgStatus) !== 'showingInstructions')
      return;

    const fetchUnconfiguredCount = async (data?: {
      location: string;
      teams: string[];
      reps: string[];
    }) => {
      const shouldRefetch = () => {
        if (!data) return true;
        if (auth.roles[auth.role] >= auth.roles.GLOBAL_ADMIN) return true;
        if (auth.roles[auth.role] === auth.roles.BRANCH_ADMIN) {
          return auth.locations?.includes(data.location);
        }
        if (auth.roles[auth.role] === auth.roles.TEAM_ADMIN) {
          return data.teams?.includes(auth.team!.toString());
        }
        return data.reps?.includes(auth.id);
      };
      if (shouldRefetch()) {
        const { unconfiguredCount, totalCount } =
          await ListingController.getUnconfiguredCount({
            includeTotal: true,
          });

        setCounts({
          configuredCount: totalCount - unconfiguredCount,
          totalCount,
        });
      }
    };

    const toggleSockets = (method: 'bind' | 'unbind') => {
      listingCountChannel[method](
        'listings_unconfigured_update',
        fetchUnconfiguredCount,
      );
    };
    const channelName = buildPusherChannel(LISTING_COUNTS_PREFIX, auth.org.id);

    if (pusherInstance && auth.org.id && !listingCountChannel) {
      const newChannel = pusherInstance.subscribe(channelName);
      setListingCountChannel(newChannel);
      fetchUnconfiguredCount();
    }

    if (listingCountChannel) {
      toggleSockets('bind');
    }
    return () => {
      if (listingCountChannel) {
        toggleSockets('unbind');
        pusherInstance.unsubscribe(channelName);
        setListingCountChannel(null);
      }
    };
  }, [pusherInstance, auth, listingCountChannel, onboarding, orgStatus]);

  return { ...counts };
};

export default useConfiguredCounts;
